import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHelmet from '@/components/base/SEOHelmet';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function AboutPage() {
  const contactFormUrl = 'https://readdy.ai/api/form/d9qqbi6e3oq4jqumtoqg';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const organizationSchema = {
    name: '国产传媒',
    url: 'https://www.guochuanmedia.com',
    description: '国产传媒是专注本土内容创作与传播的核心平台。',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
      streetAddress: '朝阳区建国路88号',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9999',
      contactType: 'customer service',
    },
  };

  const honors = [
    { year: '2025', title: '年度最具影响力内容平台', org: '中国互联网协会' },
    { year: '2024', title: '最佳内容创作生态平台', org: '中国传媒产业联盟' },
    { year: '2024', title: '数字内容创新奖', org: '国家新闻出版署' },
    { year: '2023', title: '年度优秀文化传播平台', org: '文化和旅游部' },
    { year: '2023', title: '最佳用户体验设计奖', org: '中国用户体验联盟' },
    { year: '2022', title: '内容安全治理先进单位', org: '国家互联网信息办公室' },
  ];

  return (
    <>
      <SchemaOrg type="Organization" data={organizationSchema} />
      <SEOHelmet
        title="关于我们"
        description="了解国产传媒的品牌故事、核心优势、资质荣誉与联系方式。国产传媒是专注本土内容创作与传播的核心平台。"
        keywords="关于国产传媒,国产传媒品牌介绍,国产传媒联系方式,国产传媒平台,国传媒"
        canonicalUrl="https://www.guochuanmedia.com/about"
      />

      <Navbar />
      <div className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded-full mb-3 whitespace-nowrap">
                品牌介绍
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于国产传媒
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
                国产传媒成立于2020年，是专注本土内容创作与传播的核心平台
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center max-w-[1200px] mx-auto">
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">平台使命</h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  国产传媒致力于打造中国最具影响力的本土内容生态平台。我们汇聚优质国产影视、资讯、娱乐内容，
                  为5万+创作者提供专业的创作工具和分发渠道，为品牌方提供精准高效的内容营销解决方案。
                </p>
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">核心价值</h2>
                <ul className="space-y-3">
                  {[
                    { icon: 'ri-checkbox-circle-line', text: '权威内容审核体系，确保每篇内容经过专业编辑团队审核，内容准确率99.7%' },
                    { icon: 'ri-global-line', text: '全渠道精准分发，覆盖网站、APP、30+社交媒体平台，日触达用户超5000万' },
                    { icon: 'ri-team-line', text: '深度本土化内容生态，覆盖影视、资讯、娱乐、科技、文化、商业六大核心领域' },
                    { icon: 'ri-shield-check-line', text: '数据驱动的智能推荐，基于用户兴趣和行为分析实现千人千面的内容体验' },
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-primary-500`}></i>
                      </div>
                      <span className="text-sm text-foreground-600 leading-relaxed">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20media%20office%20interior%20with%20warm%20golden%20lighting%2C%20creative%20team%20collaboration%20space%20with%20digital%20screens%2C%20contemporary%20Chinese%20aesthetic%20design%20elements%2C%20editorial%20photography%20with%20soft%20ambient%20light&width=1200&height=900&seq=about-brand-2025&orientation=landscape"
                  alt="国产传媒办公环境"
                  title="国产传媒 - 现代化办公环境"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 bg-primary-500">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { num: '5000万+', label: '日触达用户' },
                { num: '5万+', label: '入驻创作者' },
                { num: '30+', label: '分发渠道' },
                { num: '99.7%', label: '内容审核准确率' },
              ].map((stat, index) => (
                <div key={index}>
                  <div className="text-2xl md:text-3xl font-bold text-white">{stat.num}</div>
                  <div className="text-sm text-white/70 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Honors */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-full mb-3 whitespace-nowrap">
                资质荣誉
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                行业认可与荣誉
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">成立以来获得的行业荣誉与资质认证</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-[1200px] mx-auto">
              {honors.map((honor, index) => (
                <div key={index} className="flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-award-line text-lg"></i>
                  </div>
                  <div>
                    <span className="text-xs text-accent-600 font-medium">{honor.year}</span>
                    <h3 className="text-sm font-semibold text-foreground-800 mt-1">{honor.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{honor.org}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full mb-3 whitespace-nowrap">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                与我们取得联系
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                无论您是品牌方、创作者还是合作伙伴，我们都期待与您交流
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-[1200px] mx-auto">
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-5 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">contact@guochuanmedia.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">联系电话</h3>
                    <p className="text-sm text-foreground-500">400-888-9999</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座18层</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00（法定节假日除外）</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6">
                <h3 className="text-base font-semibold text-foreground-800 mb-4">发送消息</h3>
                <form
                    id="contact-form"
                    action={contactFormUrl}
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    data-readdy-form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                      if (honeypot) {
                        const msgEl = document.getElementById('contact-form-msg');
                        if (msgEl) { msgEl.textContent = '消息已发送，我们会尽快回复！'; msgEl.className = 'text-sm text-green-600 mt-3'; }
                        form.reset();
                        return;
                      }
                      const formData = new FormData(form);
                      const urlSearchParams = new URLSearchParams();
                      formData.forEach((value, key) => {
                        if (key !== 'phone_alt') urlSearchParams.append(key, value as string);
                      });
                      try {
                        const res = await fetch(contactFormUrl, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: urlSearchParams.toString(),
                        });
                        const responseText = await res.text();
                        let serverMsg = '';
                        try {
                          const parsed = JSON.parse(responseText);
                          serverMsg = parsed?.meta?.message || parsed?.message || '';
                        } catch {
                          // ignore JSON parse error
                        }
                        const msgEl = document.getElementById('contact-form-msg');
                        if (res.ok && (!serverMsg || (!serverMsg.includes('spam') && !serverMsg.includes('form data is spam')))) {
                          if (msgEl) { msgEl.textContent = '消息已发送，我们会尽快回复！'; msgEl.className = 'text-sm text-green-600 mt-3'; }
                          form.reset();
                        } else {
                          if (msgEl) { msgEl.textContent = serverMsg || '发送失败，请稍后重试'; msgEl.className = 'text-sm text-red-500 mt-3'; }
                        }
                      } catch {
                        // network error
                        const msgEl = document.getElementById('contact-form-msg');
                        if (msgEl) { msgEl.textContent = '网络错误，请稍后重试'; msgEl.className = 'text-sm text-red-500 mt-3'; }
                      }
                    }}
                  >
                    <div className="antispam-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <input
                        type="text" name="name" placeholder="您的姓名" required
                        className="px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 text-foreground-800 placeholder:text-foreground-400"
                      />
                      <input
                        type="email" name="email" placeholder="电子邮箱" required
                        className="px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 text-foreground-800 placeholder:text-foreground-400"
                      />
                    </div>
                    <input
                      type="text" name="subject" placeholder="主题" required
                      className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 text-foreground-800 placeholder:text-foreground-400 mb-3"
                    />
                    <textarea
                      name="message" placeholder="请输入您的消息（不超过500字）" rows={4} maxLength={500} required
                      className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 text-foreground-800 placeholder:text-foreground-400 resize-none mb-3"
                    ></textarea>
                    <button
                      type="submit"
                      className="w-full px-4 py-2.5 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      发送消息
                    </button>
                    <div id="contact-form-msg"></div>
                  </form>

              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      <ScrollToTopButton />
    </>
  );
}